<script setup lang="ts">
  import { Button } from '@travelata-libs/ui-kit';
  import { computed, ref } from 'vue';
  import type { DestinationPrices, SearchCriteria, SearchCriteriaHistory } from '@/entities/searchCriteria';
  import { LeadBanner } from '@/features/leadBanner';
  import { type SwitchCity } from '@/features/switchCity';
  import type { FlipDirection } from '@/shared/composables';
  import type { PageContent } from '@/shared/config/pages';
  import FlipView from '@/shared/ui/FlipView.vue';
  import { EMIRATES_SERP_URL, EmiratesBanner } from '@/widgets/banners';
  import {
    Footer,
    FooterArticlePage,
    resolveFooterArticlePage,
    type FooterArticleView,
    type FooterLinkAction,
  } from '@/widgets/footer';
  import { SearchFormMobileFlow } from '@/widgets/searchForm';
  import IndexBackground from '../IndexBackground.vue';
  import IndexMobileHeader from './IndexMobileHeader.vue';
  import IndexMobileTabs from './IndexMobileTabs.vue';
  import { type Tab, type TabType } from '../../model/types';

  interface IndexPageMobileProps {
    activeTab: TabType;
    tabs: Tab[];
    modelValue: SearchCriteria;
    isVisibleDeparture: boolean;
    isVisibleRoomButton: boolean;
    prices?: DestinationPrices;
    formUrl?: string;
    searchUrl?: string;
    resortsUrl?: string;
    accountUrl?: string;
    historySearch: SearchCriteriaHistory[];
    leadTitle: string;
    leadSubtitle: string;
    modalTitle?: string;
    modalSubtitle?: string;
    cities?: SwitchCity[];
    currentCity?: SwitchCity | null;
    pageContent?: PageContent;
    searchBtnText?: string;
    dateFieldLabel?: string;
    nightsFieldLabel?: string;
  }

  const props = withDefaults(defineProps<IndexPageMobileProps>(), {
    prices: undefined,
    modalSubtitle: '',
    formUrl: '',
    searchUrl: '',
    resortsUrl: '',
    accountUrl: '',
    modalTitle: '',
    cities: () => [],
    currentCity: null,
    pageContent: undefined,
    searchBtnText: undefined,
    dateFieldLabel: undefined,
    nightsFieldLabel: undefined,
  });

  interface IndexPageMobileEmits {
    (e: 'update:modelValue', value: SearchCriteria): void;
    (e: 'select', type: TabType): void;
    (e: 'search', value: SearchCriteria): void;
    (e: 'back'): void;
    (e: 'chat'): void;
    (e: 'change:city', city: SwitchCity): void;
  }

  defineEmits<IndexPageMobileEmits>();

  const flowRef = ref<{ openHistory?: () => void; openMenu?: () => void } | null>(null);

  const onOpenHistory = (): void => {
    flowRef.value?.openHistory?.();
  };

  const onOpenMenu = (): void => {
    flowRef.value?.openMenu?.();
  };

  const activeFooterPage = ref<FooterLinkAction | null>(null);
  const onFooterNavigate = (action: FooterLinkAction): void => {
    activeFooterPage.value = action;
  };
  const closeFooterPage = (): void => {
    activeFooterPage.value = null;
  };
  const activeFooterPageView = computed<FooterArticleView | null>(() =>
    activeFooterPage.value ? resolveFooterArticlePage(activeFooterPage.value, props.pageContent) : null,
  );
  const flipDirection = computed<FlipDirection>(() => (activeFooterPage.value ? 'forward' : 'back'));

  const openEmiratesSerp = (): void => {
    window.location.href = EMIRATES_SERP_URL;
  };
</script>

<template>
  <IndexBackground
    :tab="activeTab"
    mobile
    class="index-page-mobile"
    data-testid="index-page-mobile"
  >
    <div class="index-page-mobile__viewport">
      <div class="index-page-mobile__flow-panel">
        <SearchFormMobileFlow
          ref="flowRef"
          class="index-page-mobile__flow"
          :model-value="modelValue"
          :form-url="formUrl"
          :search-url="searchUrl"
          :resorts-url="resortsUrl"
          :account-url="accountUrl"
          :is-visible-departure="isVisibleDeparture"
          :is-visible-room-button="isVisibleRoomButton"
          :prices="prices"
          :history-search="historySearch"
          :switch-cities="cities"
          :switch-current-city="currentCity"
          :search-btn-text="searchBtnText"
          :date-field-label="dateFieldLabel"
          :nights-field-label="nightsFieldLabel"
          @update:modelValue="$emit('update:modelValue', $event)"
          @search="$emit('search', $event)"
          @change:city="$emit('change:city', $event)"
          @chat="$emit('chat')"
        >
          <template #header>
            <IndexMobileHeader
              @menu="onOpenMenu"
              @chat="$emit('chat')"
            />
            <IndexMobileTabs
              :tabs="tabs"
              :active="activeTab"
              @select="$emit('select', $event)"
            />
          </template>

          <template #button-prepend>
            <Button
              v-if="historySearch.length"
              type="button"
              class="index-page-mobile__history"
              appearance="tertiary"
              icon-left="clock"
              data-testid="index-mobile-history-btn"
              @click="onOpenHistory"
            />
          </template>

          <template #footer>
            <div class="index-page-mobile__banners">
              <LeadBanner
                :title="`${leadTitle} ${leadSubtitle}`"
                :subtitle="modalSubtitle"
                :modal-title="modalTitle"
                :mobile="true"
                class="index-page-mobile__banner"
              />

              <EmiratesBanner
                class="index-page-mobile__emirates"
                @select="openEmiratesSerp"
              />
            </div>
          </template>

          <template #pageFooter>
            <Footer
              class="index-page-mobile__footer"
              :page-content="pageContent"
              @navigate="onFooterNavigate"
            />
          </template>
        </SearchFormMobileFlow>
      </div>

      <FlipView :direction="flipDirection">
        <FooterArticlePage
          v-if="activeFooterPageView"
          :header-title="activeFooterPageView.headerTitle"
          :main-title="activeFooterPageView.mainTitle"
          :content="activeFooterPageView.content"
          @close="closeFooterPage"
          @chat="$emit('chat')"
        />
      </FlipView>
    </div>
  </IndexBackground>
</template>

<style lang="scss" scoped>
  .index-page-mobile {
    display: flex;
    flex-direction: column;
    min-height: 100dvh;
    width: 100%;

    &__viewport {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      width: 100%;
    }

    &__history {
      background: transparent;
      height: initial;
    }

    &__flow-panel {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
    }

    &__flow {
      flex: 1 1 auto;
    }

    &__banners {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);
      margin-top: var(--spacing-l);
    }

    &__banner {
      width: 100%;
    }
  }
</style>
